/**
 * Personal BYOK keys for ChatGPT and Claude — kept on the server, encrypted, and
 * never read back by the browser (migration 982); this shows only the last four
 * characters. Available to every signed-in user via Settings → My Account → My
 * Connectors. Settings → Connectors (admin) shows the same personal keys.
 */

import { useState, type ReactNode } from 'react';
import { Check, Settings, Sparkles } from 'lucide-react';
import {
  type AIProvider,
  aiKeyPreview,
  hasSavedAiKey,
  providerHasApiKey,
  removeMyAiKey,
  saveMyAiKey,
  useMyAiKeys,
} from '../../../lib/aiProviderKeys';

const ChatGPTLogo = () => (
  <svg viewBox="0 0 41 41" className="h-7 w-7" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M37.532 16.87a9.963 9.963 0 0 0-.856-8.184 10.078 10.078 0 0 0-10.855-4.835 9.964 9.964 0 0 0-6.13-3.636 10.079 10.079 0 0 0-11.784 4.958 9.962 9.962 0 0 0-6.68 4.836 10.079 10.079 0 0 0 1.24 11.817 9.965 9.965 0 0 0 .856 8.185 10.079 10.079 0 0 0 10.855 4.835 9.965 9.965 0 0 0 6.129 3.636 10.079 10.079 0 0 0 11.785-4.959 9.965 9.965 0 0 0 6.679-4.835 10.079 10.079 0 0 0-1.239-11.818zm-17.081 23.362a7.474 7.474 0 0 1-4.801-1.735c.061-.033.168-.091.237-.134l7.964-4.6a1.294 1.294 0 0 0 .655-1.134V19.054l3.366 1.944a.12.12 0 0 1 .066.092v9.299a7.505 7.505 0 0 1-7.487 7.843zm-16.124-6.874a7.474 7.474 0 0 1-.894-5.023c.06.036.162.099.237.141l7.964 4.6a1.297 1.297 0 0 0 1.308 0l9.724-5.614v3.888a.12.12 0 0 1-.048.103l-8.051 4.649a7.504 7.504 0 0 1-10.24-2.744zm-2.09-17.496a7.474 7.474 0 0 1 3.908-3.285c0 .068-.004.19-.004.274v9.201a1.294 1.294 0 0 0 .654 1.132l9.723 5.614-3.366 1.944a.12.12 0 0 1-.114.012L4.502 23.252a7.504 7.504 0 0 1-.265-7.39zm27.688 6.44-9.724-5.615 3.367-1.943a.121.121 0 0 1 .114-.012l8.048 4.648a7.498 7.498 0 0 1-1.158 13.528v-9.476a1.293 1.293 0 0 0-.647-1.13zm3.35-5.043c-.059-.037-.162-.099-.236-.141l-7.965-4.6a1.298 1.298 0 0 0-1.308 0l-9.723 5.614v-3.888a.12.12 0 0 1 .048-.103l8.05-4.645a7.497 7.497 0 0 1 11.135 7.763zm-21.063 6.929-3.367-1.944a.12.12 0 0 1-.065-.092v-9.299a7.497 7.497 0 0 1 12.293-5.756 6.94 6.94 0 0 0-.236.134l-7.965 4.6a1.294 1.294 0 0 0-.654 1.132l-.006 11.225zm1.829-3.943 4.33-2.501 4.332 2.497v4.995l-4.331 2.5-4.331-2.5V20.265z"
      fill="currentColor"
    />
  </svg>
);

const ClaudeLogo = () => (
  <svg viewBox="0 0 48 48" className="h-7 w-7" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M28.327 8L14 40h5.91l2.964-7.726h13.24L39.077 40H45L30.673 8h-2.346zm-3.644 19.337 4.817-12.566 4.817 12.566h-9.634z"
      fill="currentColor"
    />
  </svg>
);

export function MyAiProviderKeysSection() {
  const [keyModalProvider, setKeyModalProvider] = useState<AIProvider | null>(null);
  const [keyInput, setKeyInput] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Re-renders when a key is saved, removed, or the saved keys load.
  useMyAiKeys();

  const hasOpenAI = providerHasApiKey('openai');
  const hasClaude = providerHasApiKey('anthropic');
  const openaiPreview = aiKeyPreview('openai');
  const claudePreview = aiKeyPreview('anthropic');

  function openModal(provider: AIProvider) {
    setKeyModalProvider(provider);
    setKeyInput('');
    setError(null);
  }

  function closeModal() {
    setKeyModalProvider(null);
    setKeyInput('');
    setError(null);
  }

  async function saveKey() {
    if (!keyModalProvider || !keyInput.trim() || busy) return;
    setBusy(true);
    const failed = await saveMyAiKey(keyModalProvider, keyInput.trim());
    setBusy(false);
    if (failed) {
      setError(failed);
      return;
    }
    closeModal();
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  }

  async function removeKey(provider: AIProvider) {
    setBusy(true);
    const failed = await removeMyAiKey(provider);
    setBusy(false);
    if (failed) {
      setError(failed);
      return;
    }
    closeModal();
  }

  const cards: Array<{
    provider: AIProvider;
    title: string;
    subtitle: string;
    description: string;
    logo: ReactNode;
    connected: boolean;
    preview: string | null;
    accent: string;
  }> = [
    {
      provider: 'anthropic',
      title: 'Claude',
      subtitle: 'Required for whiteboard photo import',
      description:
        'Powers whiteboard photo analysis (workspace Claude or your own key) and Claude-backed AI features when Peak LLM is unavailable. Billed to your Anthropic account when using BYOK.',
      logo: <ClaudeLogo />,
      connected: hasClaude,
      preview: claudePreview,
      accent: 'amber',
    },
    {
      provider: 'openai',
      title: 'ChatGPT',
      subtitle: 'OpenAI GPT models',
      description:
        'Powers item work, clustering, and other GPT-backed assists when Peak LLM is unavailable. Billed to your OpenAI account.',
      logo: <ChatGPTLogo />,
      connected: hasOpenAI,
      preview: openaiPreview,
      accent: 'green',
    },
  ];

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-[var(--text-pri)] flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary-600" />
          My AI keys (BYOK)
        </h2>
        <p className="mt-1 text-sm text-[var(--text-ter)]">
          Optional personal API keys for AI features. Kept on our server, encrypted, and used only for your own requests — not shared with your workspace.
          Workspace admins configure shared defaults under Settings → Connectors.
        </p>
        {saved && (
          <p className="mt-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)] flex items-center gap-1">
            <Check className="h-4 w-4" /> Key saved.
          </p>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {cards.map((card) => (
          <div
            key={card.provider}
            className={` border-2 p-5 bg-[var(--bg-raised)] ${
              card.connected ? 'border-[var(--green)]' : 'border-[var(--rule)]'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-11 w-11 items-center justify-center  ${
                    card.accent === 'amber' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' : 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                  }`}
                >
                  {card.logo}
                </div>
                <div>
                  <h3 className="font-semibold text-[var(--text-pri)]">{card.title}</h3>
                  <p className="text-xs text-[var(--text-ter)]">{card.subtitle}</p>
                </div>
              </div>
              {card.connected ? (
                <span className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] px-2 py-1 rounded-full">Connected</span>
              ) : (
                <span className="text-xs text-[var(--text-ter)] bg-[var(--hover-tint)] px-2 py-1 rounded-full">Not connected</span>
              )}
            </div>
            <p className="mt-3 text-sm text-[var(--text-sec)]">{card.description}</p>
            {card.preview && (
              <p className="mt-2 text-xs font-mono text-[var(--text-sec)] bg-[var(--surface)] px-3 py-2">{card.preview}</p>
            )}
            <button
              type="button"
              onClick={() => openModal(card.provider)}
              className="mt-4 flex items-center gap-2 px-3 py-2 text-sm bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)]"
            >
              <Settings className="h-4 w-4" />
              {card.connected ? 'Update key' : 'Add API key'}
            </button>
          </div>
        ))}
      </div>

      {keyModalProvider && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-md p-6 m-4">
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">
              {keyModalProvider === 'openai' ? 'Add ChatGPT API Key' : 'Add Claude API Key'}
            </h2>
            <p className="text-sm text-[var(--text-ter)] mt-1">Kept on our server, encrypted. Once saved, only its last four characters are shown.</p>

            {(keyModalProvider === 'openai' ? openaiPreview : claudePreview) && (
              <p className="mt-3 text-sm text-[var(--text-sec)] bg-[var(--surface)] px-3 py-2 font-mono">
                Current: {keyModalProvider === 'openai' ? openaiPreview : claudePreview}
              </p>
            )}

            <input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void saveKey()}
              placeholder={keyModalProvider === 'openai' ? 'sk-...' : 'sk-ant-...'}
              autoFocus
              className="mt-4 w-full px-3 py-2 border border-[var(--rule)] font-mono text-sm"
            />
            <p className="mt-2 text-xs text-[var(--text-ter)]">
              {keyModalProvider === 'openai' ? (
                <>
                  Get a key at{' '}
                  <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                    platform.openai.com/api-keys
                  </a>
                </>
              ) : (
                <>
                  Get a key at{' '}
                  <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                    console.anthropic.com
                  </a>
                </>
              )}
            </p>

            {error && <p className="mt-3 text-sm text-[var(--coral)]">{error}</p>}

            <div className="mt-6 flex gap-2">
              <button type="button" onClick={closeModal} className="flex-1 px-4 py-2 border text-sm">
                Cancel
              </button>
              {hasSavedAiKey(keyModalProvider) && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void removeKey(keyModalProvider)}
                  className="px-4 py-2 text-[var(--coral)] border border-[var(--coral)] text-sm"
                >
                  Remove
                </button>
              )}
              <button
                type="button"
                onClick={() => void saveKey()}
                disabled={!keyInput.trim() || busy}
                className="flex-1 px-4 py-2 bg-primary-600 text-white text-sm disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
